import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import { UserInfoDataType } from "store/types/auth";

export interface AuthState {
  sessionGeneration: number;
  loggedIn: boolean;
  token: string;
  email: string;
  refresh_token: string;
  user_id: string;
  mfa_session_id: string;
  sso_login: { sso_config_id: string };
  remember_me?: boolean;
}

export const authInitialState: AuthState = {
  sessionGeneration: 0,
  loggedIn: false,
  token: "",
  email: "",
  refresh_token: "",
  user_id: "",
  mfa_session_id: "",
  sso_login: { sso_config_id: "" },
};

const computeLoggedIn = (token: string, refreshToken: string) => {
  return Boolean(token) && Boolean(refreshToken);
};

const authSlice = createSlice({
  name: "auth",
  initialState: authInitialState,
  reducers: {
    setLogin: (state, action: PayloadAction<UserInfoDataType>) => {
      const { access_token, refresh_token, user } = action.payload.data;

      if (!user) return;

      state.sessionGeneration = (state.sessionGeneration ?? 0) + 1;
      state.token = access_token;
      state.refresh_token = refresh_token;
      state.email = user.email;
      state.user_id = user.user_id;
      state.loggedIn = computeLoggedIn(access_token, refresh_token);
    },
    setToken: (state, action: PayloadAction<AuthState["token"]>) => {
      state.token = action.payload;
      state.loggedIn = computeLoggedIn(state.token, state.refresh_token);
    },
    setRefreshToken: (state, action: PayloadAction<AuthState["refresh_token"]>) => {
      state.refresh_token = action.payload;
      state.loggedIn = computeLoggedIn(state.token, state.refresh_token);
    },
    setEmail: (state, action: PayloadAction<AuthState["email"]>) => {
      state.email = action.payload;
    },
    setMfaSessionId: (state, action: PayloadAction<AuthState["mfa_session_id"]>) => {
      state.mfa_session_id = action.payload;
    },
    removeMfaSessionId: (state) => {
      state.mfa_session_id = "";
    },
    setRememberMe: (state, action: PayloadAction<boolean>) => {
      state.remember_me = action.payload;
    },
    removeRememberMe: (state) => {
      delete state.remember_me;
    },
    setSsoLogin: (state, action: PayloadAction<AuthState["sso_login"]>) => {
      state.sso_login = action.payload;
    },
    removeSsoLogin: (state) => {
      state.sso_login = { sso_config_id: "" };
    },
    logout: (state) => ({
      ...authInitialState,
      sessionGeneration: (state.sessionGeneration ?? 0) + 1,
    }),
  },
});

export const {
  setLogin,
  setToken,
  setRefreshToken,
  setEmail,
  setMfaSessionId,
  removeMfaSessionId,
  setRememberMe,
  removeRememberMe,
  setSsoLogin,
  removeSsoLogin,
  logout,
} = authSlice.actions;
export default authSlice.reducer;
